import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Input, Upload, Space, message, Modal, Form, Divider, Tooltip } from 'antd';
import { EditOutlined, UploadOutlined, PlayCircleOutlined, SoundOutlined } from '@ant-design/icons';
import { getContents, updateContent, uploadAudio } from '../../api/admin';

const { TextArea } = Input;

const ContentManage = () => {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [podcastFile, setPodcastFile] = useState(null);

  useEffect(() => {
    loadContents();
  }, []);

  const loadContents = async () => {
    try {
      setLoading(true);
      const { protocol, hostname, port } = window.location;
      const apiPort = protocol === 'https:' || port === '3443' ? '3443' : '3000';
      const apiProtocol = protocol === 'https:' || port === '3443' ? 'https' : 'http';
      const response = await fetch(`${apiProtocol}://${hostname}:${apiPort}/api/content/list`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.code === 200) {
        setContents(data.data.contents);
        // 获取综合播客文件路径（从第一条记录获取）
        if (data.data.contents.length > 0 && data.data.contents[0].podcast_path) {
          setPodcastFile(data.data.contents[0].podcast_path);
        }
      }
    } catch (error) {
      message.error('加载内容失败');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditingContent(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const response = await updateContent(editingContent.id, values);
      
      if (response.code === 200) {
        message.success('保存成功');
        setModalVisible(false);
        loadContents();
      }
    } catch (error) {
      message.error(error.message || '保存失败');
    }
  };

  // 上传单独的音频文件（用于跟读）
  const handleUploadAudio = async (file, itemNumber) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('item_number', itemNumber);
    formData.append('type', 'audio');
    
    try {
      message.loading({ content: '上传中...', key: 'upload' });
      const response = await uploadAudio(formData);
      
      if (response.code === 200) {
        message.success({ content: '音频上传成功', key: 'upload' });
        loadContents();
      }
    } catch (error) {
      message.error({ content: '上传失败', key: 'upload' });
    }
    
    return false;
  };

  // 上传综合播客文件（6项内容合并）
  const handleUploadPodcast = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'podcast');
    formData.append('is_combined', 'true'); // 标记为综合播客
    
    try {
      message.loading({ content: '上传中...', key: 'upload' });
      const response = await uploadAudio(formData);
      
      if (response.code === 200) {
        message.success({ content: '综合播客上传成功', key: 'upload' });
        loadContents();
      }
    } catch (error) {
      message.error({ content: '上传失败', key: 'upload' });
    }
    
    return false;
  };

  const columns = [
    {
      title: '序号',
      dataIndex: 'item_number',
      key: 'item_number',
      width: 60,
      render: (num) => <span style={{ fontWeight: 'bold', fontSize: 16 }}>{num}</span>
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 180,
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>
    },
    {
      title: '内容',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true
    },
    {
      title: '跟读音频',
      dataIndex: 'audio_path',
      key: 'audio_path',
      width: 200,
      render: (path, record) => (
        <Space direction="vertical" size="small">
          {path && (
            <Button
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={() => {
                const { protocol, hostname, port } = window.location;
                const apiPort = protocol === 'https:' || port === '3443' ? '3443' : '3000';
                const apiProtocol = protocol === 'https:' || port === '3443' ? 'https' : 'http';
                window.open(`${apiProtocol}://${hostname}:${apiPort}/${path}`, '_blank');
              }}
            >
              播放
            </Button>
          )}
          <Upload
            accept="audio/*"
            beforeUpload={(file) => handleUploadAudio(file, record.item_number)}
            showUploadList={false}
          >
            <Button size="small" icon={<UploadOutlined />}>
              {path ? '更新音频' : '上传音频'}
            </Button>
          </Upload>
        </Space>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Tooltip title="编辑">
          <Button 
            type="link" 
            size="small"
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
          />
        </Tooltip>
      )
    }
  ];

  return (
    <>
      {/* 综合播客上传区域 */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ margin: 0 }}>
              <SoundOutlined style={{ marginRight: 8, color: '#1890ff' }} />
              综合播客文件
            </h3>
            <p style={{ margin: '8px 0 0', color: '#666', fontSize: 13 }}>
              上传包含学习内容的综合播客文件（音频或视频），用于步骤2"听播客"学习
            </p>
          </div>
          <Space>
            {podcastFile && (
              <Button 
                icon={<PlayCircleOutlined />}
                onClick={() => window.open(podcastFile, '_blank')}
              >
                播放当前播客
              </Button>
            )}
            <Upload
              accept="audio/*,video/*"
              beforeUpload={handleUploadPodcast}
              showUploadList={false}
            >
              <Button type="primary" icon={<UploadOutlined />}>
                {podcastFile ? '更新播客' : '上传播客'}
              </Button>
            </Upload>
          </Space>
        </div>
        {podcastFile && (
          <div style={{ marginTop: 12, padding: '8px 12px', background: '#f5f5f5', borderRadius: 4 }}>
            <span style={{ color: '#666' }}>当前文件: </span>
            <span style={{ color: '#1890ff' }}>{podcastFile.split('/').pop()}</span>
          </div>
        )}
      </Card>

      {/* 准则内容管理表格 */}
      <Card>
        <h2 style={{ marginBottom: 16 }}>学习内容</h2>
        <Table
          columns={columns}
          dataSource={contents}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      {/* 编辑弹窗 */}
      <Modal
        title={`编辑准则 ${editingContent?.item_number}`}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label="标题"
            rules={[{ required: true, message: '请输入标题' }]}
          >
            <Input placeholder="请输入准则标题" />
          </Form.Item>
          
          <Form.Item
            name="content"
            label="内容"
            rules={[{ required: true, message: '请输入内容' }]}
          >
            <TextArea rows={6} placeholder="请输入准则详细内容" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ContentManage;
